import type { ReactElement } from 'react';
import type { GetServerSideProps, GetServerSidePropsResult as GSSPResult } from 'next';
import Head from 'next/head';
import { useQuery } from '@tanstack/react-query';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { EntityType } from '@eventbrite/cerberus-service-pysoa-client';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { getLocaleFromRequest } from '@eventbrite/webapp-locales';
import { MetaTags, OpenGraphTags, TwitterTags, TwitterCard } from '@eventbrite/webapp-public-content';

import { EVENT_STATUS } from '@/src/listings/types';

import { logger } from '@/src/listings/common/logger';
import { buildListingsEventListingServiceClient } from '@/src/listings/clients/eventListing';
import { resolveListingsStage } from '@/src/listings/runtime/stage';
import { setCacheControl } from '@/src/listings/utils/cacheControl';
import setCanonicalUrl from '@/src/listings/utils/canonicalUrl';
import { type EventListingError, handle404Error, handleError } from '@/src/listings/utils/error-handling';
import { SLUG_ROUTE_PATTERN, webappMetricsLogger } from '@/src/listings/utils/webapp-metrics';
import { getSoaTokenFromHeaders, getUserIdFromHeaders, isListingsDebugRequestedFromHeaders } from '@/src/listings/utils/ebRequestHeaders';
import {
  createResilientCerberusClient,
  createResilientEventListingServiceClient,
} from '@/src/listings/utils/resilientDependencyClients';
import { Header } from '@/src/listings/components/Header';
import { ProtectedEventGate } from '@/src/listings/components/ProtectedEventGate';
import { getBaseDeepLinkAppUrl } from '@/src/listings/components/SEOTags';
import { buildSocialShareUrl, getSocialImageUrl } from '@/src/listings/components/SEOTags/socialImageMetadata';
import { useEventDescription, useEventTitle } from '@/src/listings/hooks';

import { QUERY_PARAMS } from '@/src/listings/common/constants';
import { getLocaleMessages } from '@/src/listings/i18n/locale';

import { sanitizeAndValidateEventId } from '@/src/listings/validation/slug';
import { DEFAULT_LOCALE } from '@eventbrite/webapp-locales/dist/constants';
import type { ListingPageServerProps, ListingPageProps } from '@/src/listings/pageTypes';
import type { ProtectedEventInfo } from '@/src/listings/pageTypes';
import { buildListingPagePropsFromContext, sharedListingRequest } from '@/src/listings/pageSupport/buildListingPageProps';
import { EventListingPage } from '@/src/listings/components/EventListingPage/EventListingPage';
import { WtSurvey } from '@/src/listings/components/WtSurvey';
import { EditorRefreshBannerContent } from '@/src/listings/components/NotificationBanner/notificationBannerUseCases';
import { NotificationBannerFrame } from '@/src/listings/components/NotificationBanner';
import type { BasicInfo } from '@/src/listings/types';
import { getUserDataQueryOptions } from '@/src/listings/hooks/useUserData/useUserData';
import { fetchFreshEventContext } from '@/src/listings/utils/fetchFreshEventContext';
import { getGlobalHeaderServerSideProps } from '@eventbrite/consumer-header.global-header/server';
import { getCerberusClient, hasEventPermission } from '@/src/listings/pageSupport/cerberus';
import { EVENT_DETAILS_READ_PERMISSION } from '@/src/listings/pageSupport/constants';
import { isNonEmptyString } from '@/src/listings/utils/apiRouteUtils';
import { createDependencyMetricsLogger } from '@/src/listings/utils/dependencyMetrics';
import { getAppStage } from '@/src/listings/utils/serviceClientConfig';
import { dependencyMetrics } from '@/src/listings/pageSupport/eventRouteUtils';
import { TimeoutError } from '@/src/listings/utils/resiliency';
import { fetchMoreOrganizerEvents } from '@/src/listings/services/moreOrganizerEvents';
import { isOrganizerEventsSsrEnabled } from '@/src/listings/utils/organizerEventsSsr';
import { isCreatorEventSearchForOrganizerEventsEnabled } from '@/src/listings/utils/organizerEventsRetrieval';
import type { OrganizerEventResponse } from '@/src/listings/components/MoreOrganizerEvents/types';

import { ListingsLayout } from '@/src/listings/shell/ListingsLayout';
import { withListingsShell } from '@/src/listings/shell/serverData';
import { withListingsDocumentTelemetry } from '@/src/listings/utils/withListingsDocumentTelemetry';
import type { GetLayoutContext, NextPageWithLayout } from '@/pages/_app.page';

type GetServerSidePropsResult = GetServerSideProps<ListingPageServerProps>;
const PROTECTED_EDITOR_CONTEXT_QUERY_KEY = 'protected-editor-event-context';
const CERBERUS_TIMEOUT_MS = 2500;
const ELS_CONTEXT_TIMEOUT_MS = 5000;
const ELS_CONTEXT_TIMEOUT_MESSAGE = 'Event listing context timed out';

/** Hydration-call budget for the organizer-events SSR fetch. Sized to the
 *  event-hydration-service P95 (30–50ms cache-hot, >95% cache hit rate). */
const ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS = 150;

/** Overall TTFB budget for the SSR organizer-events fetch (SOA proxy +
 *  hydration combined). Caps worst-case page TTFB contribution at this value
 *  regardless of the SOA proxy client's 3s default. Sized to capture roughly
 *  p75 of `call.event.get_organizer_future_events` (p50 131ms, p75 216ms);
 *  renders past this budget fall through to the client-side lazy fetch. */
const ORGANIZER_EVENTS_SSR_DEADLINE_MS = 500;

function hasUsableProtectedEditorContext(context: WebappResponse | null | undefined): context is WebappResponse {
  if (!context?.basicInfo) {
    return false;
  }

  const protectedEditorData = [
    context.structuredContent != null,
    context.salesStatus != null,
    context.goodToKnow != null,
    context.taxonomies != null,
    context.performers != null,
    context.presenterInfo != null,
    Array.isArray(context.tags),
    Array.isArray(context.collections),
  ];

  return protectedEditorData.some(Boolean);
}

const log404Error = (cause: string, eventId: string | number | null, additionalData?: Record<string, unknown>) => {
  logger.warn('Render 404', {
    cause,
    eventId: eventId?.toString() || 'unknown',
    ...additionalData,
  });
};

/**
 * Checks if the current request is from a logged-in user who has editor
 * permissions on the given event via Cerberus. Returns `true` only when
 * credentials are present AND Cerberus grants `event.details:read`.
 */
const isRequestFromEditor = async (
  headers: Record<string, string | string[] | undefined>,
  eventId: string,
): Promise<boolean> => {
  const userId = getUserIdFromHeaders(headers) ?? '';
  const soaToken = getSoaTokenFromHeaders(headers) ?? '';

  if (!isNonEmptyString(userId) || !isNonEmptyString(soaToken)) {
    return false;
  }

  try {
    const resilientCerberus = createResilientCerberusClient(getCerberusClient(), dependencyMetrics, {
      permissions: CERBERUS_TIMEOUT_MS,
      user: CERBERUS_TIMEOUT_MS,
    });
    const permissionsResponse = await resilientCerberus.calculatePermissions({
      context: { auth_token: soaToken },
      body: {
        permissions: [EVENT_DETAILS_READ_PERMISSION],
        entities: [{ type: EntityType.EVENT, id: eventId }],
      },
    });
    return hasEventPermission(permissionsResponse, eventId, EVENT_DETAILS_READ_PERMISSION);
  } catch (error) {
    logger.warn('Slug: Cerberus editor check failed, treating as non-editor', { eventId, error });
    return false;
  }
};

const getServerSidePropsInner = (async ({ res, params, query, req }) => {
  const stage = stageFromString(process.env.STAGE || Stage.TEST);
  const backendStage = resolveListingsStage();
  const startTime = Date.now();
  const { slug } = params || {};
  const eventId = sanitizeAndValidateEventId(slug);
  const keepTld = query.keep_tld === 'true';
  const skipRedirection = stage === Stage.LOCAL || query.skip_redirection === 'true';
  // `?debug=true` responses must not be shared-cached: the listings document
  // cache key excludes the query string, so a stored debug response would be
  // served to non-debug viewers; same protection as the homepage's
  // `setHomepageDocumentCacheControl`. The listings origin-request policy strips
  // the query string, so the trusted edge-issued header is what actually marks a
  // debug request on listings — accept either signal.
  const debug = query.debug === 'true' || isListingsDebugRequestedFromHeaders(req.headers);
  const ebHost = (req.headers['eb-host'] || req.headers['host']) as string;

  let _globalHeaderPropsCache: Awaited<ReturnType<typeof getGlobalHeaderServerSideProps>> | undefined;
  const getGlobalHeaderPropsOnce = async () => {
    if (_globalHeaderPropsCache === undefined) {
      _globalHeaderPropsCache = await getGlobalHeaderServerSideProps({ req: sharedListingRequest(req), res, query, resolvedUrl: '/' });
    }
    return _globalHeaderPropsCache;
  };

  // Initial locale comes from the request (or defaults), but we only
  // fetch translation messages once we know the final locale.
  const localeFromRequest = getLocaleFromRequest(sharedListingRequest(req)) || DEFAULT_LOCALE;
  const locale = localeFromRequest;
  let messages: Record<string, string> = {};
  if (!eventId) {
    log404Error('INVALID_EVENT_ID', null, {
      slug: typeof slug === 'string' ? slug : Array.isArray(slug) ? slug[0] : 'undefined',
    });
    webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Invalid event ID', startTime, 404);
    try {
      messages = await getLocaleMessages(locale);
    } catch (error) {
      logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, {
        error,
      });
    }
    return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
  }
  // Embedded Metrics Logger will send AWS Metrics under this namespace
  const metrics = createDependencyMetricsLogger(getAppStage());
  const eventListingServiceClient = createResilientEventListingServiceClient(
    buildListingsEventListingServiceClient(backendStage, metrics),
    metrics,
    ELS_CONTEXT_TIMEOUT_MS,
  );
  const getEventContextWebWithTimeout = async (ebSoaToken?: string) => {
    try {
      return await eventListingServiceClient.getEventContextWeb(eventId.toString(), {}, ebSoaToken);
    } catch (error) {
      if (error instanceof TimeoutError) {
        throw Object.assign(error, {
          statusCode: 503,
          error: ELS_CONTEXT_TIMEOUT_MESSAGE,
        });
      }
      throw error;
    }
  };

  /**
   * Split-gated SSR fetch for the "More events from this organizer" section.
   * Returns `null` when the gate is off, the organizer is missing, the overall
   * deadline fires, or the upstream call errors — letting the client-side
   * lazy fetch populate the section. The overall deadline caps the page's
   * worst-case TTFB contribution from this fetch (see
   * `ORGANIZER_EVENTS_SSR_DEADLINE_MS`); the per-step hydration timeout
   * (`ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS`) still applies inside it.
   */
  const fetchOrganizerEventsForSsrOrNull = async (context: WebappResponse): Promise<OrganizerEventResponse | null> => {
    const organizerId = context.basicInfo?.organizer?.id;
    if (!organizerId) {
      return null;
    }

    const ssrEnabled = await isOrganizerEventsSsrEnabled(eventId.toString(), context.basicInfo?.organizationId);
    if (!ssrEnabled) {
      return null;
    }

    const useCreatorEventSearch = await isCreatorEventSearchForOrganizerEventsEnabled({
      eventId: eventId.toString(),
      organizerId,
    });

    let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
    const deadlinePromise = new Promise<null>((resolve) => {
      deadlineTimer = setTimeout(() => resolve(null), ORGANIZER_EVENTS_SSR_DEADLINE_MS);
    });

    try {
      const result = await Promise.race([
        fetchMoreOrganizerEvents({
          eventId: eventId.toString(),
          organizerId,
          soaToken: undefined,
          stage: backendStage,
          metrics,
          hydrationTimeoutMs: ORGANIZER_EVENTS_SSR_HYDRATION_TIMEOUT_MS,
          useCreatorEventSearch,
        }),
        deadlinePromise,
      ]);
      if (result === null) {
        logger.info('Slug: organizer-events SSR fetch hit overall deadline, falling back to client', { eventId });
      }
      return result;
    } catch (error) {
      if (error instanceof TimeoutError) {
        logger.info('Slug: organizer-events SSR fetch timed out, falling back to client', { eventId });
      } else {
        logger.warn('Slug: organizer-events SSR fetch failed, falling back to client', { eventId, error });
      }
      return null;
    } finally {
      if (deadlineTimer) {
        clearTimeout(deadlineTimer);
      }
    }
  };

  /**
   * Helper: given a successful ELS context, handle all rendering paths
   * (draft-preview, protected, redirect, normal).
   */
  const handleContext = async (
    context: WebappResponse,
    isAuthenticated: boolean,
  ): Promise<GSSPResult<ListingPageServerProps>> => {
    if (!context || !context.basicInfo) {
      if (!context) {
        log404Error('SERVICE_RETURNED_NULL', eventId);
      } else {
        log404Error('MISSING_BASIC_INFO', eventId);
      }
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Event not found or invalid status', startTime, 404);
      try {
        messages = await getLocaleMessages(locale);
      } catch (error) {
        logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, { error });
      }
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }

    if (context.basicInfo.status === EVENT_STATUS.DRAFT || context.basicInfo.status === EVENT_STATUS.UNSAVED) {
      if (isAuthenticated) {
        logger.info('Slug: rendering draft event in preview mode for editor', { eventId });
        setCacheControl(res, context, { forceNoCache: true });
        const baseProps = await buildListingPagePropsFromContext({ req, res, query, context });
        webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
        return {
          props: {
            ...baseProps,
            isPreview: true,
          },
        };
      }

      log404Error(
        context.basicInfo.status === EVENT_STATUS.DRAFT ? 'EVENT_STATUS_DRAFT' : 'EVENT_STATUS_UNSAVED',
        eventId,
      );
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, 'Event not found or invalid status', startTime, 404);
      try {
        messages = await getLocaleMessages(locale);
      } catch (error) {
        logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, { error });
      }
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }

    if (context.basicInfo.isProtected) {
      if (await isRequestFromEditor(req.headers, eventId.toString())) {
        try {
          const soaToken = getSoaTokenFromHeaders(req.headers);
          const authContext = await getEventContextWebWithTimeout(soaToken);
          if (authContext?.basicInfo && !authContext.basicInfo.isProtected) {
            setCacheControl(res, authContext, { forceNoCache: true });
            setCanonicalUrl(res, authContext);
            const initialMoreOrganizerEvents = await fetchOrganizerEventsForSsrOrNull(authContext);
            const baseProps = await buildListingPagePropsFromContext({
              req,
              res,
              query,
              context: authContext,
              initialMoreOrganizerEvents,
            });
            webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
            return { props: baseProps };
          }
        } catch (retryError) {
          logger.warn('Slug: authenticated ELS retry failed for editor on protected event', {
            eventId,
            error: retryError,
          });
        }
      }

      const slugStr = typeof slug === 'string' ? slug : Array.isArray(slug) && slug[0] ? slug[0] : '';
      const protectedEvent = buildProtectedEventInfo(context, slugStr);
      setCacheControl(res, context, { forceNoCache: query.forceNoCache === 'true' || debug });
      setCanonicalUrl(res, context);
      const baseProps = await buildListingPagePropsFromContext({ req, res, query, context, sharedDocument: true });
      webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);
      return {
        props: {
          ...baseProps,
          context: null,
          protectedEvent,
        },
      };
    }

    const forcedError =
      query[QUERY_PARAMS.FORCE_ERROR] && !isNaN(Number(query[QUERY_PARAMS.FORCE_ERROR]))
        ? {
            statusCode: Number(query[QUERY_PARAMS.FORCE_ERROR]),
            error: 'Forced Error by Query Param',
          }
        : null;

    if (forcedError) {
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, forcedError.error, startTime, forcedError.statusCode);
      try {
        messages = await getLocaleMessages(locale);
      } catch (error) {
        logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, {
          error,
        });
      }
      return handleError(res, forcedError, locale, messages, await getGlobalHeaderPropsOnce());
    }

    setCacheControl(res, context, { forceNoCache: query.forceNoCache === 'true' || isAuthenticated || debug });
    setCanonicalUrl(res, context);

    // Redirection logic
    let canonicalUrl = buildUrl(context?.basicInfo?.url);
    if (canonicalUrl && !skipRedirection) {
      const canonicalUrlSlug = canonicalUrl.pathname.split('/').pop();

      if (keepTld && ebHost) {
        canonicalUrl = new URL(canonicalUrl.toString().replace(canonicalUrl.hostname, ebHost as string));
      }

      if (canonicalUrlSlug !== slug || canonicalUrl.hostname !== ebHost) {
        webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 301);
        return {
          redirect: {
            destination: canonicalUrl.toString(),
            statusCode: 301,
          },
        };
      }
    }

    const initialMoreOrganizerEvents = await fetchOrganizerEventsForSsrOrNull(context);
    const props = await buildListingPagePropsFromContext({
      req,
      res,
      query,
      context,
      initialMoreOrganizerEvents,
      sharedDocument: !isAuthenticated,
    });
    webappMetricsLogger.logRenderSuccess(SLUG_ROUTE_PATTERN, startTime, 200);

    return { props };
  };

  try {
    // First attempt: no SOA token so ELS can serve from its DynamoDB cache.
    const context = await getEventContextWebWithTimeout();
    return await handleContext(context, false);
  } catch (error) {
    const errorStatusCode = (error as EventListingError).statusCode || 500;

    // For 403/404 errors, check if this is an editor viewing a draft event.
    // Only then do we retry with the SOA token (which bypasses the ELS cache).
    if (errorStatusCode === 403 || errorStatusCode === 404) {
      if (await isRequestFromEditor(req.headers, eventId.toString())) {
        try {
          const soaToken = getSoaTokenFromHeaders(req.headers);
          const context = await getEventContextWebWithTimeout(soaToken);
          return await handleContext(context, true);
        } catch (retryError) {
          const retryErrorStatusCode = (retryError as EventListingError).statusCode || 500;
          logger.warn('Slug: authenticated ELS retry failed for editor', { eventId, error: retryError });
          if (retryErrorStatusCode !== 403 && retryErrorStatusCode !== 404) {
            webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, retryError, startTime, retryErrorStatusCode);
            return handleError(res, retryError as EventListingError, locale, {}, await getGlobalHeaderPropsOnce());
          }
        }
      }

      log404Error(errorStatusCode === 403 ? 'SERVICE_THREW_403' : 'SERVICE_THREW_404', eventId, {
        slug: typeof slug === 'string' ? slug : Array.isArray(slug) ? slug.join(',') : 'unknown',
      });
      webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, error, startTime, 404);
      try {
        messages = await getLocaleMessages(locale);
      } catch (localeError) {
        logger.error(`--locale-- Failed to get messages for locale ${locale} for invalid event`, {
          error: localeError,
        });
      }
      return handle404Error(res, locale, messages, await getGlobalHeaderPropsOnce(), req.headers);
    }
    webappMetricsLogger.logRenderError(SLUG_ROUTE_PATTERN, error, startTime, errorStatusCode);
    return handleError(res, error as EventListingError, locale, {}, await getGlobalHeaderPropsOnce());
  }
}) satisfies GetServerSidePropsResult;

export const getServerSideProps = withListingsDocumentTelemetry(
  withListingsShell<ListingPageServerProps>(getServerSidePropsInner),
);

function buildProtectedEventInfo(context: WebappResponse, slugStr: string): ProtectedEventInfo {
  const { basicInfo, gallery } = context;
  if (!basicInfo) {
    throw new Error('buildProtectedEventInfo requires context.basicInfo');
  }
  const status = basicInfo.status ?? '';
  const isEnded =
    status === EVENT_STATUS.ENDED || status === EVENT_STATUS.COMPLETED || status === EVENT_STATUS.CANCELLED;

  const images = gallery?.images as
    { croppedLogoUrl480?: string; croppedLogoUrl600?: string; croppedLogoUrl940?: string }[] | undefined;
  const primaryImageUrl = getSocialImageUrl(images);

  return {
    eventId: basicInfo.id!,
    slug: slugStr,
    name: basicInfo.name ?? '',
    summary: basicInfo.summary ?? '',
    startDate: basicInfo.startDate!,
    endDate: basicInfo.endDate!,
    hideStartDate: basicInfo.hideStartDate ?? false,
    hideEndDate: basicInfo.hideEndDate ?? false,
    locale: basicInfo.locale ?? 'en_US',
    url: basicInfo.url ?? '',
    isOnline: basicInfo.isOnline ?? false,
    isSeries: basicInfo.isSeries ?? false,
    ...(basicInfo.venue && {
      venue: {
        name: basicInfo.venue.name,
        address: {
          city: basicInfo.venue.address?.city,
          region: basicInfo.venue.address?.region,
        },
      },
    }),
    ...(basicInfo.organizer && {
      organizer: {
        id: basicInfo.organizer.id,
        name: basicInfo.organizer.name,
        url: basicInfo.organizer.url,
      },
    }),
    ...(primaryImageUrl && { backgroundImageUrl: primaryImageUrl }),
    ...(primaryImageUrl &&
      basicInfo.url && {
        socialShareImageUrl: buildSocialShareUrl(primaryImageUrl, basicInfo.url),
      }),
    isEnded,
  };
}

function buildUrl(url: string): URL | null {
  try {
    return new URL(url);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
}

const Page: NextPageWithLayout<ListingPageProps> = (props: ListingPageProps): ReactElement => {
  const protectedEvent = props.protectedEvent;
  const seoBasicInfo = protectedEvent
    ? ({
        name: protectedEvent.name,
        summary: protectedEvent.summary ?? '',
        startDate: protectedEvent.startDate,
        endDate: protectedEvent.endDate,
        hideStartDate: protectedEvent.hideStartDate,
        hideEndDate: protectedEvent.hideEndDate ?? false,
        isOnline: protectedEvent.isOnline ?? false,
        isSeries: protectedEvent.isSeries ?? false,
        venue: protectedEvent.venue,
        organizer: {
          name: protectedEvent.organizer?.name ?? '',
        },
        isProtected: false,
      } as BasicInfo)
    : undefined;
  const title = useEventTitle(seoBasicInfo, props.locale);
  const description = useEventDescription(seoBasicInfo, props.locale);
  const { data: userData } = useQuery(
    getUserDataQueryOptions(protectedEvent?.eventId, protectedEvent?.isOnline, !!protectedEvent),
  );
  const { data: protectedEditorContext, isFetching: isRefreshingProtectedEditorContext } = useQuery({
    queryKey: [PROTECTED_EDITOR_CONTEXT_QUERY_KEY, protectedEvent?.eventId],
    queryFn: async () => {
      if (!protectedEvent?.eventId) {
        return null;
      }

      return fetchFreshEventContext(protectedEvent.eventId);
    },
    enabled: !!protectedEvent?.eventId && !!userData?.canManageEvent,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (protectedEvent && hasUsableProtectedEditorContext(protectedEditorContext)) {
    return (
      <EventListingPage
        {...props}
        context={protectedEditorContext}
        protectedEvent={undefined}
        skipFreshEditorContextFetch
      />
    );
  }

  if (protectedEvent) {
    const { globalHeaderProps, query } = props;
    const { name, url, socialShareImageUrl } = protectedEvent;
    const canonicalUrl = props.canonicalUrl && props.canonicalUrl.trim().length > 0 ? props.canonicalUrl : url;

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="twitter:url" content={canonicalUrl} />
        </Head>
        <MetaTags showDefaultDescription={false} />
        <OpenGraphTags
          url={canonicalUrl}
          title={name}
          description={description}
          imageUrl={socialShareImageUrl}
          type="events.event"
        />
        <TwitterTags
          description={description}
          title={name}
          imageUrl={socialShareImageUrl}
          iosAppUrl={getBaseDeepLinkAppUrl(protectedEvent.eventId, query ?? {})('eiosurlxtcar')}
          androidAppUrl={getBaseDeepLinkAppUrl(protectedEvent.eventId, query ?? {})('eandurlxtcar')}
          twitterCard={TwitterCard.SummaryLargeImage}
        />
        <Header globalHeaderProps={globalHeaderProps} />
        {isRefreshingProtectedEditorContext && (
          <NotificationBannerFrame testId="protected-editor-refresh-banner">
            <EditorRefreshBannerContent />
          </NotificationBannerFrame>
        )}
        <ProtectedEventGate event={protectedEvent} />
      </>
    );
  }
  return (
    <>
      {/* This is the public-listing branch only. The editor preview and the
          password gate return earlier and must not show the survey. */}
      <WtSurvey />
      <EventListingPage {...props} />
    </>
  );
};

Page.getLayout = (page: ReactElement, { route }: GetLayoutContext) => (
  <ListingsLayout page={page} route={route} />
);

export default Page;
